/**
 * Client-side helpers for turning picked/dropped/pasted files into
 * {@link Attachment} objects the prompt inputs send to the orchestrator.
 *
 * Text kinds are read as UTF-8 and inlined into the LLM prompt server-side;
 * images/other are base64-encoded, stored, and previewed but not fed to the
 * model (see the backend attachment context renderer).
 */
import type { Attachment, AttachmentKind } from '../types';

/** Max size for a single attachment (bytes). Matches the server-side cap. */
export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
/**
 * Max total raw bytes across one submission. The orchestrator's request body
 * limit is 64 MB and base64 inflates payloads ~4/3, so a maxed-out set of files
 * (10 × 5 MB = 50 MB raw ≈ 68 MB encoded) would 413 with a generic error. Cap
 * the raw total well under that so oversized sets are caught with a clear message.
 */
export const MAX_TOTAL_ATTACHMENT_BYTES = 40 * 1024 * 1024;
/** Max attachments per prompt submission. */
export const MAX_ATTACHMENTS = 10;
/** Extracted text is truncated to this many chars to keep prompts bounded. */
export const MAX_EXTRACTED_CHARS = 200_000;

/**
 * Raster image types we accept. SVG and other image types are rejected — SVG
 * can carry script, and only these raster formats are sent to the vision models.
 * Kept in sync with the server allowlist.
 */
const RASTER_IMAGE_MIMES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
const RASTER_IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp']);

/** File extensions we treat as inline-able text even when the MIME type lies. */
const TEXT_EXTENSIONS = new Set([
  'txt', 'text', 'md', 'markdown', 'mdx', 'csv', 'tsv', 'json', 'jsonl',
  'yaml', 'yml', 'xml', 'html', 'htm', 'css', 'log', 'ini', 'toml', 'env',
  'ts', 'tsx', 'js', 'jsx', 'mjs', 'cjs', 'py', 'java', 'go', 'rb', 'rs',
  'c', 'h', 'cpp', 'cc', 'hpp', 'cs', 'php', 'swift', 'kt', 'sh', 'bash',
  'sql', 'graphql', 'gql', 'vue', 'svelte',
]);

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** Whether a file is a PDF (text is extracted server-side, then inlined). */
export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || extensionOf(file.name) === 'pdf';
}

/** Office document extensions whose text is extracted server-side, then inlined. */
const OFFICE_EXTENSIONS = new Set(['docx', 'xlsx', 'xls', 'pptx']);
const OFFICE_MIMES = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);

/** Whether a file is a supported Office doc (.docx/.xlsx/.xls/.pptx). */
export function isOfficeFile(file: File): boolean {
  return OFFICE_MIMES.has(file.type) || OFFICE_EXTENSIONS.has(extensionOf(file.name));
}

/** Whether a file is a supported raster image (png/jpeg/gif/webp — not SVG). */
export function isRasterImage(file: File): boolean {
  return RASTER_IMAGE_MIMES.has(file.type) || RASTER_IMAGE_EXTENSIONS.has(extensionOf(file.name));
}

/** Classify a file into the broad kind that drives preview + prompt handling. */
export function classifyFile(file: File): AttachmentKind {
  if (isRasterImage(file)) return 'image';
  const ext = extensionOf(file.name);
  if (
    file.type.startsWith('text/') ||
    file.type === 'application/json' ||
    file.type === 'application/xml' ||
    TEXT_EXTENSIONS.has(ext)
  ) {
    return 'text';
  }
  return 'other';
}

/**
 * Whether the app accepts this file. Supported: raster images (png/jpeg/gif/
 * webp), PDFs and Office docs (.docx/.xlsx/.pptx — text extracted server-side),
 * and text/code files. Everything else (SVG, zip, binaries, etc.) is rejected —
 * never uploaded. Mirrors the server-side allowlist.
 */
export function isFileAccepted(file: File): boolean {
  return isRasterImage(file) || isPdfFile(file) || isOfficeFile(file) || classifyFile(file) === 'text';
}

/** `accept` attribute values for the file input, per menu option. */
export const ACCEPT_IMAGES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', '.png', '.jpg', '.jpeg', '.gif', '.webp'].join(',');
export const ACCEPT_DOCUMENTS = [
  'application/pdf',
  '.pdf',
  '.docx',
  '.xlsx',
  '.xls',
  '.pptx',
  ...[...OFFICE_MIMES],
  'text/*',
  ...[...TEXT_EXTENSIONS].map((e) => `.${e}`),
].join(',');
export const ACCEPT_ALL = [ACCEPT_IMAGES, ACCEPT_DOCUMENTS].join(',');

/** Human-readable size, e.g. "12.3 KB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      // Strip the "data:<mime>;base64," prefix — we send only the payload.
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

let idSeq = 0;
function nextId(): string {
  idSeq += 1;
  return `att_${Date.now().toString(36)}_${idSeq}`;
}

export interface FilesToAttachmentsResult {
  attachments: Attachment[];
  /** User-facing messages for files that were rejected (too big, etc.). */
  errors: string[];
}

/**
 * Convert picked/dropped/pasted files into Attachment objects, enforcing the
 * per-file size cap. `existingCount` lets callers enforce the total cap across
 * successive additions.
 */
export async function filesToAttachments(
  files: File[],
  existingCount = 0,
  existingBytes = 0,
): Promise<FilesToAttachmentsResult> {
  const attachments: Attachment[] = [];
  const errors: string[] = [];
  let runningBytes = existingBytes;

  for (const file of files) {
    if (!isFileAccepted(file)) {
      errors.push(`"${file.name}" is not a supported file type — skipped.`);
      continue;
    }
    if (existingCount + attachments.length >= MAX_ATTACHMENTS) {
      errors.push(`Only ${MAX_ATTACHMENTS} attachments allowed — "${file.name}" skipped.`);
      continue;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      errors.push(`"${file.name}" is ${formatBytes(file.size)} — over the ${formatBytes(MAX_ATTACHMENT_BYTES)} limit.`);
      continue;
    }
    if (runningBytes + file.size > MAX_TOTAL_ATTACHMENT_BYTES) {
      errors.push(
        `Adding "${file.name}" would exceed the ${formatBytes(MAX_TOTAL_ATTACHMENT_BYTES)} total attachment limit — skipped.`,
      );
      continue;
    }
    runningBytes += file.size;
    const kind = classifyFile(file);
    try {
      const base: Attachment = {
        id: nextId(),
        name: file.name || 'untitled',
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
        kind,
      };
      if (kind === 'text') {
        const text = await file.text();
        base.extractedText = text.length > MAX_EXTRACTED_CHARS ? text.slice(0, MAX_EXTRACTED_CHARS) : text;
        // Keep the raw bytes too so the file can be previewed/downloaded later.
        base.dataBase64 = await readAsBase64(file);
      } else {
        base.dataBase64 = await readAsBase64(file);
      }
      attachments.push(base);
    } catch {
      errors.push(`Could not read "${file.name}".`);
    }
  }

  return { attachments, errors };
}

/** Build a `data:` URL for previewing an image attachment. */
export function imagePreviewUrl(a: Attachment): string | null {
  if (a.kind !== 'image' || !a.dataBase64) return null;
  return `data:${a.mimeType};base64,${a.dataBase64}`;
}
